import { API_URL, DEAFULT_API_VERSION } from './config';
import axios from 'axios';
import router from './router';
import {useUserStore} from "@/stores/user";

const Base = {
  apiVersion: (version = DEAFULT_API_VERSION) => `/${version}`,
};

const axiosInstance = axios.create({
  // default axios configurations
  baseURL: `${API_URL}/api${Base.apiVersion()}`,
  withCredentials: true,
  // timeout: 3000,
});

// intercept responses and check for authn/authz
// force logoff, if 401 or 403 received
axiosInstance.interceptors.response.use(function (res) {
  return res
}, function (error) {
  if (error?.response?.config?.skipAuthRedirect) {
    return Promise.reject(error)
  }

  if (error.response.status === 401 || error.response.status === 403) {
    const userStore = useUserStore();
    userStore.forceLogoff();
    router.push('/signin')
  }
  return Promise.reject(error)
})

const API = {
  getFeatureFlags: () => {
    return `/config/feature-flags`
  },
  getStates: () => {
    return `/pagecontrols/users/details`;
  },
  getStateByAbbreviation: (state) => {
    return `/pagecontrols/states/${state}`
  },
  getAgencies: () => {
    return `/agencies`;
  },
  getRoles: () => {
    return `/pagecontrols/users/details`;
  },
  getEnrollments: () => {
    return `/enrollments`;
  },
  getEnrollment: (id) => {
    return `/enrollments/${id}`;
  },
  getEnrollmentStatus: () => {
    return `/pagecontrols/enrollments`;
  },
  archiveEnrollment: (id) => {
    return `/enrollments/${id}`;
  },
  removeNhepStatus: (id) => {
    return `/enrollments/${id}/nhep`
  },
  getEnrollees: (id) => {
    return `/enrollees/${id}`;
  },
  getEnrolleesList: (id) => {
    return `/enrollees/?enrollmentId=${id}`;
  },
  getPackets: () => {
    return `/packets`;
  },
  getPacketsById: () => {
    return `/packets`;
  },
  getPacket: (id) => {
    return `/packets/${id}`;
  },
  createPacket: () => {
    return `/packets`;
  },
  updatePacket: (id) => {
    return `/packets/${id}`;
  },
  getDocuments: () => {
    return `/documents`;
  },
  getDocumentsByEnrollee: (id) => {
    return `/enrollee/documents/${id}`
  },
  createDocument: () => {
    return `/documents`;
  },
  updateDocument: (id) => {
    return `/documents/${id}`;
  },
  getUsers: () => {
    return `/user`;
  },
  getUser: (id) => {
    return `/user/${id}`;
  },
  updateUser: (id) => {
    return `/user/${id}`;
  },
  getLanguages: () => {
    return `/pagecontrols/users/details`;
  },
  archiveDocument: (id) => {
    return `/documents/${id}`;
  },
  archivePacket: (id) => {
    return `/packets/${id}`;
  },
  addAgenciesToPacket: (id) => {
    return `/packet/agencies/${id}`;
  },
  getAgenciesByPacketId: (id) => {
    return `/packet/agencies/${id}`;
  },
  deleteAgencyFromPacket: (id) => {
    return `/packet/agencies/${id}`;
  },
  getAgencyListForPackets: () => {
    return `/packet/agencies`;
  },
  setDefaultPacket: (id) => {
    return `/packet/agencies/${id}/default`;
  },
  updateDocumentOrder: () => {
    return `/packet/documents/sort`;
  },
  deleteDocumentFromPacket: (id) => {
    return `/packet/documents/${id}`;
  },
  assignDocumentToPacket: (id) => {
    return `/packet/documents/${id}`;
  },
  assignDocumentToEnrollee: (id) => {
    return `/enrollee/documents/${id}`;
  },
  addNotes: () => {
    return `/notes/`;
  },
  getNotes: () => {
    return `/notes/`;
  },
  getAgentList: () => {
    return `/available-agent`;
  },
  getParticipantNotes: () => {
    return `/notes`;
  },
  updateEnrollee: (id) => {
    return `/enrollments/presentation/${id}`;
  },
  getRace: () => {
    return `/pagecontrols/races`;
  },
  getCaregiverRelationshipTypes: () => {
    return `/pagecontrols/caregiver/relationship-types`;
  },
  getCaregiverLegalResponsibilityTypes: () => {
    return `/pagecontrols/caregiver/legal/responsibility-types`;
  },
  getCaregiverResponsibilityTypes: () => {
    return `/pagecontrols/caregiver/responsibility-types`;
  },
  saveParticipant: (id) => {
    return `/enrollees/${id}`;
  },
  saveCaregiver: (id) => {
    return `/enrollees/${id}`;
  },
  addNewCaregiver: () => {
    return `/enrollees`
  },
  updateStatusAndScheduleType: () => {
    return `/enrollments/`;
  },
  updateAssignment: (id) => {
    return `/enrollment/specialist/${id}`;
  },
  saveVendor: (id) => {
    return `/enrollees/${id}`;
  },
  assignPacket: (id) => {
    return `/enrollee/${id}/packet`;
  },
  getCaregivers: (id) => {
    return `/enrollees/caregiver/${id}`;
  },
  assignCaregiversToEnrollment: (id) => {
    return `/enrollees/caregiver/${id}`;
  },
  scheduleAppointment: () => {
    return `/calendar/create-event`;
  },
  fetchAgentCalendarEvents: () => {
    return `/calendar/get-events`;
  },
  createBulkCalendarEvents: () => {
    return `/calendar/bulk-create`
  },
  uploadDocuments: () => {
    return `/enrollee/documents`;
  },
  removeEnrolleeDocuments: (id) => {
    return `/enrollee/documents/${id}`;
  },
  rescheduleAppointment: () => {
    return `/calendar/delete-events`;
  },
  getDocumentCustomQuestions: (enrolleeId, documentId) => {
    return `/enrollees/${enrolleeId}/documents/${documentId}/prompts`;
  },
  getDocumentNames: () => {
    return `/documents/names`;
  },
  saveCustomPromptResponse: (enrolleeId, documentId) => {
    return `/enrollees/${enrolleeId}/documents/${documentId}/responses`;
  },
  setSelfService: () => {
    return `/update-self-service`;
  },
  sendEmails: () => {
    return `/send-emails`;
  },
  sendCaregiverEmails: () => {
    return `/send-caregiver-emails`;
  },
  sendEmail: () => {
    return `/send-email`;
  },
  sendEmailsMass: (type) => `/send-emails-mass/${type}`,
  verifyEnrolleeDetails: () => {
    return `/self-service-email-login`;
  },
  fetchUserInfo: () => {
    return `/user-info`
  },

  removeEnrolleeFromEnrollment: (id) => {
    return `/enrollees/${id}`;

  },

  enrollmentParticpant: (enrollmentId) => {
    return `/enrollments/${enrollmentId}/participant`;
  },

  returnEnrollmentToActive: (enrollmentId) => {
    return `/enrollment/activate/${enrollmentId}`
  },

  getEmployerRelationship: () => {
    return `/config/relationships/`;
  },

  // getEnrollmentProgress: (enrollmentId) => {
  //   return;
  // },

  getEnrolleeEnrollmentProgress: (enrolleeId) => {
    return `/enrollee/${enrolleeId}/progress`
  },

  updateEnrollmentProgress: () => {
    return `/enrollee/setComplete`;
  },

  samlLogin: () => `/saml/login`,
  logout: () => `/logout`,
  userCheck: () => `/user-check`,

  parsePdfDocument: () => `/documents/parse`,

  docusignGetConsoleView: () => `/docusign/console-view`,
  docusignGetSenderView: (envelopeId) => `/docusign/sender-view/${envelopeId}`,
  docusignGetRecipientView: (envelopeId) => `/docusign/recipient-view/${envelopeId}`,
  docusignCreateEnvelope: () => `/docusign/envelope`,
  docusignEmbedSignType: (enrolleeId) => `/docusign/sign-type/${enrolleeId}`,
  docusignCallbackUrl: (envelopeData) => `/docusign/callback/${envelopeData}`,
  docusignAuthCodeReset: () => `/docusign/auth-code`,
  nullifyDocusignForEnrollee: (enrolleeId) => `/docusign/nullify/${enrolleeId}`,

  eNitialGetSignerLink: () => `/docusign/signer-link`,

  confirmCreate: () => `/confirm`,
  getConfirmResponseById: (id) => `/confirm/${id}`,
  checkEnrolleeExists: (id, state) => `/confirm/${id}/exists?state=${state}`,
  getConfirmConfigurations: (state_abbreviation) => `/confirm/config/${state_abbreviation}`,

  fetchAzurePhotoById: (usersId) => '/api' + `/azure-photo/${usersId}`,
};

const apiErrorHandler = (error) => { console.error(error); throw error; }

export { axiosInstance as axios, API, apiErrorHandler };
